import { varKey, varLabel, varOptions } from "common/var";
import {
  Badge,
  Button,
  Input,
  TablePanel,
  Select,
  MonthPicker,
} from "components/common";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { callGetApiWithAuth } from "utils/api";
import { asDate } from "utils/text";
import StatCard from "./stat/StatCard";
import moment from "moment";

export default function SubscriptionReportPage() {
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [paginationParam, setPaginationParam] = useState({
    currentPage: 1,
    perPage: 15,
    total: 0,
  });
  const [searchParam, setSearchParam] = useState({
    uuid: "",
    level: "",
    name: "",
    type: "",
    month: moment().startOf("month").format("YYYY-MM-DD"),
  });

  const [isExporting, setIsExporting] = useState(false);

  const loadTableData = (paginationParam0, searchParam0) => {
    setIsLoading(true);
    const params = {
      ...searchParam0,
      page: paginationParam0.currentPage,
      per_page: paginationParam0.perPage,
      month: moment(searchParam.month).startOf("month").format("YYYY-MM"),
    };
    const q = Object.keys(params)
      .map((k) => k + "=" + params[k])
      .join("&");
    callGetApiWithAuth(
      `report/subscription_report/list?${q}`,
      ({ data }) => {
        setTableData(data.data);
        setPaginationParam({
          ...paginationParam,
          currentPage: data.current_page,
          total: data.total,
        });
        setIsLoading(false);
      },
      () => {
        setIsLoading(false);
      }
    );
  };
  const handleSearch = () => {
    const paginationParam0 = {
      ...paginationParam,
      currentPage: 1,
    };
    loadTableData(paginationParam0, searchParam);
  };
  const onPageChange = (page) => {
    const paginationParam0 = {
      ...paginationParam,
      currentPage: page,
    };
    loadTableData(paginationParam0, searchParam);
  };

  useEffect(() => {
    handleSearch();
  }, []);

  const onExportDone = (res) => {
    setIsExporting(false);
    window.open(res.data.csv_file, "_blank");
  };
  const onExportFail = () => {
    setIsExporting(false);
  };

  const handleExport = () => {
    setIsExporting(true);

    const params = {
      ...searchParam,
      month: moment(searchParam.month).startOf("month").format("YYYY-MM"),
    };
    const q = Object.keys(params)
      .map((k) => k + "=" + params[k])
      .join("&");

    callGetApiWithAuth(
      `report/subscription_report/export?${q}`,
      onExportDone,
      onExportFail
    );
  };

  const onChangeMonth = (v) => {
    const searchParam0 = {
      ...searchParam,
      month: moment(v).startOf("month").format("YYYY-MM-DD"),
    };

    setSearchParam(searchParam0);
  };

  return (
    <Wrapper>
      <StatCard />
      <TablePanel
        title={"Subscription Report"}
        data={tableData}
        loading={isLoading}
        paginationParam={paginationParam}
        onPageChange={onPageChange}
        toolbar={
          <div
            className="d-flex align-items-center flex-wrap"
            style={{ gap: "8px" }}
          >
            <div>
              <Input
                value={searchParam.level}
                onChange={(e) => {
                  setSearchParam({
                    ...searchParam,
                    level: e.target.value,
                  });
                }}
                placeholder={"Level"}
                style={{ width: 100 }}
              />
            </div>
            <div>
              <Select
                label="User Type*"
                value={searchParam.type}
                onChange={(value) =>
                  setSearchParam({ ...searchParam, type: value })
                }
                size="large"
                showSearch
                options={[
                  {
                    label: "Select User Type",
                    value: "",
                  },
                  ...varOptions("user.type"),
                ]}
                optionFilterProp="label"
              />
            </div>
            <div>
              <Input
                value={searchParam.uuid}
                onChange={(e) => {
                  setSearchParam({
                    ...searchParam,
                    uuid: e.target.value,
                  });
                }}
                placeholder={"By User ID, Username"}
                style={{ width: 200 }}
              />
            </div>
            <div>
              <Input
                value={searchParam.name}
                onChange={(e) => {
                  setSearchParam({
                    ...searchParam,
                    name: e.target.value,
                  });
                }}
                placeholder={"By Name"}
                style={{ width: 200 }}
              />
            </div>
            <div>
              <MonthPicker
                date={searchParam.month}
                onChange={(v) => onChangeMonth(v)}
                size="large"
              />
            </div>
            <div className="d-flex gap-2">
              <Button loading={isLoading} onClick={handleSearch}>
                Search
              </Button>
              <Button loading={isExporting} onClick={handleExport}>
                Export
              </Button>
            </div>
          </div>
        }
        columns={[
          {
            title: "Level",
            render: (_, record) => <span>{record.depth}</span>,
          },
          {
            title: "User ID",
            render: (_, record) => <span>{record.uuid}</span>,
          },
          {
            title: "Username",
            render: (_, record) => <span>{record.username}</span>,
          },
          {
            title: "Name",
            render: (_, record) => (
              <span>
                {record.first_name}&nbsp;
                {record.last_name}
              </span>
            ),
          },
          {
            title: "Email",
            render: (_, record) => <span>{record.email}</span>,
          },
          {
            title: "Subscription",
            render: (_, record) => <span>{record.subscription_name}</span>,
          },
          {
            title: "Status",
            render: (_, record) => (
              <span>
                <Badge
                  type={"subscriptionStatus"}
                  keyName={varKey("subscription.status", record.status)}
                  label={varLabel("subscription.status", record.status)}
                />
              </span>
            ),
          },
          {
            title: "Service Status",
            render: (_, record) => (
              <span>
                {" "}
                <Badge
                  type={"subscriptionServiceStatus"}
                  keyName={varKey(
                    "subscription.service_status",
                    record.service_status
                  )}
                  label={varLabel(
                    "subscription.service_status",
                    record.service_status
                  )}
                />
              </span>
            ),
          },
          {
            title: "Last Billing Date",
            render: (_, record) => <span>{asDate(record.paid_at)}</span>,
          },
          {
            title: "Next Billing Date",
            render: (_, record) => <span>{asDate(record.next_date)}</span>,
          },
        ]}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  .ant-picker {
    height: 48px !important;
    border-radius: 8px !important;
    .ant-picker-input > input {
      color: #919eab !important;
    }
  }
`;
